/**
 * PermissionTesterTab Component
 * 
 * Admin panel tab for testing user permissions
 * 
 * Features:
 * - Select user and test if they have specific permissions
 * - Optional project context
 * - Shows allowed/denied result with explanation
 * - Lists all contributing rules
 * 
 * Created: 2026-01-26 (Permission System V2 Phase 3)
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { useToast } from '../../hooks/useToast';
import { MENU_STRUCTURE } from '../../config/menuStructure';
import { canSeeMenuItem } from '../../utils/menuAccess';

function tokensMatch(haystack, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const hay = String(haystack || '').toLowerCase();
  return q.split(/\s+/).every((token) => hay.includes(token));
}

function userLabel(user) {
  if (!user) return '';
  const name = user.display_name || [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email;
  return user.role_name ? `${name} (${user.role_name})` : name;
}

function projectLabel(project) {
  if (!project) return '';
  return [project.job_number, project.job_name].filter(Boolean).join(' — ');
}

const PICKER_INPUT = {
  width: '100%',
  padding: '0.5rem 2rem 0.5rem 0.5rem',
  border: '1px solid #ddd',
  borderRadius: '4px',
  fontSize: '0.9rem',
  boxSizing: 'border-box',
};

function SearchableSelect({
  label,
  required,
  placeholder,
  value,
  displayValue,
  onChange,
  items,
  emptyLabel,
  allowFreeText = false,
}) {
  const [query, setQuery] = useState(displayValue || '');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) setQuery(displayValue || '');
  }, [displayValue, open]);

  const filtered = useMemo(() => {
    const list = items.filter((item) => tokensMatch(item.searchText || item.title, query));
    return list.slice(0, 50);
  }, [items, query]);

  const commitEmpty = () => {
    onChange('');
    setQuery('');
    setOpen(false);
  };

  const commitItem = (item) => {
    onChange(item.id, item);
    setQuery(item.title);
    setOpen(false);
    setHi(0);
  };

  const commitFreeText = () => {
    const typed = query.trim();
    if (allowFreeText && typed) {
      onChange(typed);
      setQuery(typed);
    } else if (!value) {
      setQuery('');
    } else {
      setQuery(displayValue || '');
    }
    setOpen(false);
  };

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
        {label}{required ? ' *' : ''}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          value={query}
          placeholder={placeholder}
          autoComplete="off"
          onFocus={() => {
            setOpen(true);
            setHi(0);
            inputRef.current?.select();
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!allowFreeText) onChange('');
            else onChange(e.target.value);
            setOpen(true);
            setHi(0);
          }}
          onKeyDown={(e) => {
            if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
              setOpen(true);
              return;
            }
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setHi((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setHi((i) => Math.max(i - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              const pick = filtered[hi];
              if (pick) commitItem(pick);
              else commitFreeText();
            } else if (e.key === 'Escape') {
              setOpen(false);
              setQuery(displayValue || '');
            }
          }}
          style={PICKER_INPUT}
        />
        {query && (
          <button
            type="button"
            onClick={() => {
              commitEmpty();
              inputRef.current?.focus();
              setOpen(true);
            }}
            title="Clear"
            style={{
              position: 'absolute',
              right: '6px',
              top: '50%',
              transform: 'translateY(-50%)',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              color: '#6c757d',
              fontSize: '1rem',
              lineHeight: 1,
              padding: '2px 4px',
            }}
          >
            ×
          </button>
        )}
      </div>
      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          zIndex: 20,
          marginTop: '2px',
          maxHeight: '240px',
          overflowY: 'auto',
          backgroundColor: 'white',
          border: '1px solid #ddd',
          borderRadius: '4px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        }}>
          {emptyLabel && (
            <button
              type="button"
              onMouseEnter={() => setHi(-1)}
              onClick={commitEmpty}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '0.5rem 0.75rem',
                border: 'none',
                cursor: 'pointer',
                backgroundColor: hi === -1 ? '#e7f1ff' : 'white',
                fontSize: '0.85rem',
                color: '#6c757d',
                fontStyle: 'italic',
              }}
            >
              {emptyLabel}
            </button>
          )}
          {filtered.length === 0 ? (
            <div style={{ padding: '0.75rem', color: '#6c757d', fontSize: '0.85rem' }}>
              {query.trim() ? 'No matches' : 'Nothing to show'}
            </div>
          ) : (
            filtered.map((item, index) => {
              const active = index === hi;
              const chosen = item.id === value;
              return (
                <button
                  key={item.id}
                  type="button"
                  onMouseEnter={() => setHi(index)}
                  onClick={() => commitItem(item)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '0.5rem 0.75rem',
                    border: 'none',
                    cursor: 'pointer',
                    backgroundColor: active ? '#e7f1ff' : (chosen ? '#f0f7ff' : 'white'),
                    fontSize: '0.85rem',
                  }}
                >
                  <div style={{ fontWeight: 600, color: '#212529' }}>{item.title}</div>
                  {item.subtitle && (
                    <div style={{ color: '#6c757d', fontSize: '0.75rem' }}>{item.subtitle}</div>
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// Common permission suggestions
const PERMISSION_SUGGESTIONS = [
  'view_all', 'edit_all', 'delete', 'approve', 'export',
  'matrix.upload', 'matrix.edit', 'matrix.delete', 'matrix.approve',
  'admin', 'manage_users', 'system_config',
  'upload', 'create', 'edit', 'view',
  'view_assigned', 'edit_own', 'approve_own_trades',
  'edit_schedule', 'edit_payroll', 'edit_payables',
  'create_incidents', 'create_inspections'
];

// Resource type suggestions
const RESOURCE_TYPES = [
  'ProgressTracking',
  'Budget',
  'Schedule',
  'Timesheet',
  'Document',
  'Report',
  'Material',
  'PurchaseOrder'
];

const PermissionTesterTab = () => {
  const { instance, accounts } = useMsal();
  const { showToast } = useToast();
  
  // Data state
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Form state
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [permission, setPermission] = useState('');
  const [resourceType, setResourceType] = useState('');
  
  // Result state
  const [exploring, setExploring] = useState(false);
  const [profile, setProfile] = useState(null);
  const [who, setWho] = useState(null);
  const [menuFilter, setMenuFilter] = useState('');

  // Fetch users and projects
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [usersRes, projectsRes] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', '/users/all'),
        authenticatedApiCall(instance, accounts, 'GET', '/projects')
      ]);
      
      if (usersRes.data.success) {
        setUsers(usersRes.data.data || []);
      }
      if (projectsRes.data.success) {
        setProjects(projectsRes.data.projects || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      showToast('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, showToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const loadExplorer = useCallback(async () => {
    if (!selectedUser && !permission && !selectedProject && !resourceType) {
      setProfile(null);
      setWho(null);
      return;
    }
    try {
      setExploring(true);
      const params = new URLSearchParams();
      if (selectedUser) params.append('user_id', selectedUser);
      if (selectedProject) params.append('project_id', selectedProject);
      if (permission.trim()) params.append('action', permission.trim());
      if (resourceType) params.append('resource_type', resourceType);
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `/permissions/access-explorer?${params.toString()}`
      );
      if (response.data.success) {
        setProfile(response.data.data.profile || null);
        setWho(response.data.data.who || null);
      } else {
        showToast(response.data.message || 'Failed to load access', 'error');
      }
    } catch (err) {
      console.error('Error loading access:', err);
      showToast('Failed to load access', 'error');
    } finally {
      setExploring(false);
    }
  }, [selectedUser, selectedProject, permission, resourceType, instance, accounts, showToast]);

  useEffect(() => {
    const timer = setTimeout(() => { loadExplorer(); }, 300);
    return () => clearTimeout(timer);
  }, [loadExplorer]);

  // Clear form
  const handleClear = () => {
    setSelectedUser('');
    setSelectedProject('');
    setPermission('');
    setResourceType('');
    setMenuFilter('');
    setProfile(null);
    setWho(null);
  };

  const selectedUserDetails = users.find(u => u.id === selectedUser);
  const selectedProjectDetails = projects.find(p => String(p.id) === String(selectedProject));

  const userItems = useMemo(() => users.map((user) => ({
    id: user.id,
    title: user.display_name || user.email,
    subtitle: [user.email, user.role_name, user.lt_employee_code].filter(Boolean).join(' · '),
    searchText: [
      user.display_name, user.email, user.first_name, user.last_name,
      user.role_name, user.lt_employee_code, user.lt_employee_name, user.department_name,
    ].filter(Boolean).join(' '),
  })), [users]);

  const projectItems = useMemo(() => projects.map((project) => ({
    id: project.id,
    title: projectLabel(project),
    subtitle: project.client_name || project.status || '',
    searchText: [project.job_number, project.job_name, project.client_name, project.status].filter(Boolean).join(' '),
  })), [projects]);

  const permissionItems = useMemo(() => PERMISSION_SUGGESTIONS.map((p) => ({
    id: p,
    title: p,
    searchText: p,
  })), []);

  const resourceItems = useMemo(() => RESOURCE_TYPES.map((type) => ({
    id: type,
    title: type,
    searchText: type,
  })), []);

  const result = profile?.action_check || null;

  // Render contributing rule
  const renderRule = (rule, index) => {
    const getTypeLabel = (type) => {
      const labels = {
        role_permissions: 'Role Permissions',
        project_assignment: 'Project Assignment',
        role_override: 'Role Override',
        trade_assignment: 'Trade Assignment',
        permission_override: 'Permission Override'
      };
      return labels[type] || type;
    };

    const getTypeColor = (type) => {
      const colors = {
        role_permissions: '#007bff',
        project_assignment: '#6f42c1',
        role_override: '#fd7e14',
        trade_assignment: '#20c997',
        permission_override: '#e83e8c'
      };
      return colors[type] || '#6c757d';
    };

    return (
      <div
        key={index}
        style={{
          padding: '0.75rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          marginBottom: '0.5rem',
          borderLeft: `4px solid ${getTypeColor(rule.type)}`
        }}
      >
        <div style={{ 
          fontWeight: 600, 
          color: getTypeColor(rule.type),
          marginBottom: '0.5rem'
        }}>
          {getTypeLabel(rule.type)}
        </div>
        
        {rule.type === 'role_permissions' && (
          <div style={{ fontSize: '0.85rem' }}>
            <div>
              <strong>Roles:</strong> {rule.source?.join(', ') || 'N/A'}
            </div>
            <div>
              <strong>Sample Permissions:</strong> {rule.permissions?.join(', ') || 'None'}
            </div>
            <div>
              <strong>Result:</strong>{' '}
              <span style={{ color: rule.result ? '#28a745' : '#dc3545' }}>
                {rule.result ? 'Has Permission' : 'Does Not Have Permission'}
              </span>
            </div>
          </div>
        )}
        
        {rule.type === 'project_assignment' && (
          <div style={{ fontSize: '0.85rem' }}>
            <div>
              <strong>Project ID:</strong> {rule.project_id}
            </div>
            <div>
              <strong>Assigned:</strong>{' '}
              <span style={{ color: rule.has_assignment ? '#28a745' : '#dc3545' }}>
                {rule.has_assignment ? 'Yes' : 'No'}
              </span>
            </div>
            {rule.role_override_id && (
              <div>
                <strong>Role Override:</strong> {rule.role_override_id}
              </div>
            )}
          </div>
        )}
        
        {rule.type === 'role_override' && (
          <div style={{ fontSize: '0.85rem' }}>
            <div>
              <strong>Override Role:</strong> {rule.role_id}
            </div>
            <div>
              <strong>Sample Permissions:</strong> {rule.permissions?.join(', ') || 'None'}
            </div>
            <div>
              <strong>Result:</strong>{' '}
              <span style={{ color: rule.result ? '#28a745' : '#dc3545' }}>
                {rule.result ? 'Has Permission' : 'Does Not Have Permission'}
              </span>
            </div>
          </div>
        )}
        
        {rule.type === 'trade_assignment' && (
          <div style={{ fontSize: '0.85rem' }}>
            <div>
              <strong>Assigned Trades:</strong> {rule.assigned_trades?.join(', ') || 'None'}
            </div>
            <div>
              <strong>Requested Trade:</strong> {rule.requested_trade}
            </div>
            <div>
              <strong>Has Trade:</strong>{' '}
              <span style={{ color: rule.result ? '#28a745' : '#dc3545' }}>
                {rule.result ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
        )}
        
        {rule.type === 'permission_override' && (
          <div style={{ fontSize: '0.85rem' }}>
            <div>
              <strong>Override ID:</strong> {rule.override_id}
            </div>
            <div>
              <strong>Type:</strong>{' '}
              <span style={{ 
                color: rule.granted ? '#28a745' : '#dc3545',
                fontWeight: 600
              }}>
                {rule.granted ? 'GRANT' : 'DENY'}
              </span>
            </div>
            {rule.reason && (
              <div>
                <strong>Reason:</strong> {rule.reason}
              </div>
            )}
            {rule.expires_at && (
              <div>
                <strong>Expires:</strong> {new Date(rule.expires_at).toLocaleDateString()}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
        Loading...
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ margin: 0, marginBottom: '0.5rem' }}>Permission Tester</h3>
        <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
          The boxes are filters. Pick a person to see their access. Pick an action to see who has it. Resource type only narrows exceptions, not menus.
        </p>
      </div>

      {/* Test Form */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem',
        padding: '1.5rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px'
      }}>
        <SearchableSelect
          label="User"
          placeholder="Search by name, email, or role..."
          value={selectedUser}
          displayValue={userLabel(selectedUserDetails)}
          onChange={(id) => setSelectedUser(id)}
          items={userItems}
        />

        <SearchableSelect
          label="Project Context"
          placeholder="Search by job number or name..."
          value={selectedProject}
          displayValue={projectLabel(selectedProjectDetails)}
          onChange={(id) => setSelectedProject(id)}
          items={projectItems}
          emptyLabel="No Project (Global)"
        />

        <SearchableSelect
          label="Permission/Action"
          placeholder="Search or type e.g. matrix.upload"
          value={permission}
          displayValue={permission}
          onChange={(id) => setPermission(id)}
          items={permissionItems}
          allowFreeText
        />

        <SearchableSelect
          label="Resource Type"
          placeholder="Exceptions only, not menus"
          value={resourceType}
          displayValue={resourceType}
          onChange={(id) => setResourceType(id)}
          items={resourceItems}
          emptyLabel="Any Resource"
        />
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem' }}>
        <button
          onClick={handleClear}
          style={{
            padding: '0.4rem 0.9rem',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}
        >
          Clear
        </button>
        {exploring && <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>Loading access…</span>}
      </div>

      {profile && (
        <AccessProfile
          profile={profile}
          menuFilter={menuFilter}
          onMenuFilter={setMenuFilter}
          onPickUser={null}
        />
      )}

      {who && (
        <WhoList who={who} onPick={(id) => setSelectedUser(id)} />
      )}

      {/* Result Display */}
      {profile?.action_check && (
        <div style={{ 
          border: `2px solid ${result.allowed ? '#28a745' : '#dc3545'}`,
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          {/* Result Header */}
          <div style={{
            padding: '1.5rem',
            backgroundColor: result.allowed ? '#d4edda' : '#f8d7da',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '4rem',
              marginBottom: '0.5rem'
            }}>
              {result.allowed ? '✓' : '✗'}
            </div>
            <div style={{
              fontSize: '1.5rem',
              fontWeight: 600,
              color: result.allowed ? '#155724' : '#721c24'
            }}>
              {result.allowed ? 'ALLOWED' : 'DENIED'}
            </div>
            <div style={{
              marginTop: '0.5rem',
              color: result.allowed ? '#155724' : '#721c24',
              fontSize: '0.95rem'
            }}>
              {result.reason}
            </div>
          </div>

          {/* Query Details */}
          <div style={{ padding: '1rem', backgroundColor: '#f8f9fa' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', fontSize: '0.9rem' }}>
              <div>
                <strong>User:</strong> {selectedUserDetails?.display_name || selectedUserDetails?.email}
              </div>
              <div>
                <strong>Permission:</strong> <code>{result.action}</code>
              </div>
              {result.project_id && (
                <div>
                  <strong>Project:</strong> {result.project_id}
                </div>
              )}
              {result.resource_type && (
                <div>
                  <strong>Resource:</strong> {result.resource_type}
                </div>
              )}
            </div>
          </div>

          {/* Contributing Rules */}
          <div style={{ padding: '1rem' }}>
            <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>
              Contributing Rules
            </h4>
            {result.contributing_rules && result.contributing_rules.length > 0 ? (
              result.contributing_rules.map((rule, index) => renderRule(rule, index))
            ) : (
              <div style={{ color: '#666', fontStyle: 'italic' }}>
                No contributing rules found.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tips */}
      {!profile && !who && !exploring && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#e7f3ff',
          borderRadius: '8px',
          color: '#004085'
        }}>
          <strong>Tips:</strong>
          <ul style={{ margin: '0.5rem 0 0 1.5rem', padding: 0 }}>
            <li>Select a person to see the menus, actions, projects, and exceptions they have</li>
            <li>Select only an action to list who has it, then click a name to open their access</li>
            <li>Resource type filters exceptions. It does not line up with menu names</li>
            <li>Add an action on a person to see allowed or denied, and why</li>
          </ul>
        </div>
      )}
    </div>
  );
};

function visibleMenuTree(items, userPermissions) {
  const out = [];
  for (const item of items || []) {
    const children = visibleMenuTree(item.subItems, userPermissions);
    const self = canSeeMenuItem(item.id, {
      userPermissions,
      permissionsLoaded: true,
      featureFlags: {},
      userEmail: userPermissions.email,
    });
    if (self || children.length) {
      out.push({ id: item.id, label: item.label, children });
    }
  }
  return out;
}

function filterMenuTree(nodes, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return nodes;
  const out = [];
  for (const node of nodes) {
    const children = filterMenuTree(node.children, q);
    if (node.label.toLowerCase().includes(q) || children.length) {
      out.push({ ...node, children });
    }
  }
  return out;
}

function MenuBranch({ node, depth, forceOpen }) {
  const [open, setOpen] = useState(forceOpen);
  useEffect(() => { setOpen(forceOpen); }, [forceOpen]);
  const hasKids = node.children && node.children.length > 0;
  return (
    <div>
      <div style={{ display: 'flex', gap: '6px', padding: '2px 0 2px ' + (depth * 14) + 'px', fontSize: '13px' }}>
        {hasKids ? (
          <button type="button" onClick={() => setOpen((v) => !v)} style={{ border: 'none', background: 'none', cursor: 'pointer', width: '14px', padding: 0, color: '#6b7280' }}>
            {open ? '▼' : '▶'}
          </button>
        ) : <span style={{ width: '14px' }} />}
        <span>{node.label}</span>
      </div>
      {hasKids && open && node.children.map((child) => (
        <MenuBranch key={child.id} node={child} depth={depth + 1} forceOpen={forceOpen} />
      ))}
    </div>
  );
}

function AccessProfile({ profile, menuFilter, onMenuFilter }) {
  const userPermissions = useMemo(() => ({
    email: profile.user.email,
    isSuperadmin: profile.user.is_superadmin,
    isAdmin: profile.user.is_admin,
    menuItems: profile.menu_grants || [],
    allDepartmentIds: profile.department_ids || [],
    subDepartments: (profile.sub_departments || []).map((row) => row.id),
  }), [profile]);

  const menus = useMemo(() => {
    const tree = visibleMenuTree(MENU_STRUCTURE, userPermissions);
    return filterMenuTree(tree, menuFilter);
  }, [userPermissions, menuFilter]);

  const section = { marginTop: '1rem' };
  const heading = { margin: '0 0 0.35rem', fontSize: '0.95rem' };

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
      <div style={{ fontWeight: 600 }}>
        {profile.user.display_name}
        <span style={{ fontWeight: 400, color: '#6b7280' }}>
          {' '}· {profile.user.role_name || 'No role'} · {profile.user.authority_name || 'No authority'}
        </span>
      </div>
      <div style={{ fontSize: '13px', color: '#4b5563', marginTop: '4px' }}>
        Departments: {profile.departments.length
          ? profile.departments.map((dept) => `${dept.name} (${dept.via})`).join(', ')
          : 'None'}
      </div>
      <div style={{ fontSize: '13px', color: '#4b5563' }}>
        Sub-departments: {profile.sub_departments.length
          ? profile.sub_departments.map((row) => row.name).join(', ')
          : 'None'}
      </div>

      <div style={section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'center' }}>
          <h4 style={heading}>Menus they can see</h4>
          <input
            value={menuFilter}
            onChange={(e) => onMenuFilter(e.target.value)}
            placeholder="Filter menus"
            style={{ ...PICKER_INPUT, width: '180px', paddingRight: '0.5rem' }}
          />
        </div>
        {profile.menu_grants?.includes('*') && (
          <div style={{ fontSize: '12px', color: '#92400e', marginBottom: '4px' }}>Role grants all menus. Department rules still apply.</div>
        )}
        {menus.length === 0 ? (
          <div style={{ fontSize: '13px', color: '#6b7280' }}>No menus match.</div>
        ) : menus.map((node) => (
          <MenuBranch key={node.id} node={node} depth={0} forceOpen={!!menuFilter.trim()} />
        ))}
      </div>

      <div style={section}>
        <h4 style={heading}>Actions</h4>
        <div style={{ fontSize: '13px', color: '#111' }}>
          {profile.permissions_wildcard && !profile.permissions.includes('*')
            ? `All actions (*), filtered view: ${profile.permissions.join(', ') || 'no name match'}`
            : (profile.permissions.length ? profile.permissions.join(', ') : 'None')}
        </div>
      </div>

      <div style={section}>
        <h4 style={heading}>Projects {profile.projects.length ? `(${profile.projects.length})` : ''}</h4>
        {profile.projects.length === 0 ? (
          <div style={{ fontSize: '13px', color: '#6b7280' }}>No matching project assignments.</div>
        ) : (
          <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '13px' }}>
            {profile.projects.slice(0, 40).map((project) => (
              <li key={project.id}>
                {[project.job_number, project.job_name].filter(Boolean).join(' — ')}
                {project.override_role_name ? ` · role on job: ${project.override_role_name}` : ''}
              </li>
            ))}
            {profile.projects.length > 40 && <li>+{profile.projects.length - 40} more</li>}
          </ul>
        )}
      </div>

      <div style={section}>
        <h4 style={heading}>Exceptions</h4>
        {profile.overrides.length === 0 ? (
          <div style={{ fontSize: '13px', color: '#6b7280' }}>No matching exceptions.</div>
        ) : (
          <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '13px' }}>
            {profile.overrides.map((row) => (
              <li key={row.id || `${row.permission}-${row.project_id}-${row.resource_type}`}>
                <strong style={{ color: row.granted ? '#15803d' : '#b91c1c' }}>{row.granted ? 'Grant' : 'Deny'}</strong>
                {' '}{row.permission}
                {row.resource_type ? ` · ${row.resource_type}` : ''}
                {row.job_number ? ` · ${row.job_number}` : (row.project_id ? '' : ' · any project')}
                {row.reason ? ` · ${row.reason}` : ''}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function viaLabel(via) {
  const labels = {
    authority: 'Authority',
    exception_grant: 'Exception grant',
    exception_deny: 'Exception deny',
    project: 'On this project',
    exception: 'Exception',
  };
  if (Array.isArray(via)) return via.map((item) => labels[item] || item).join(', ');
  return labels[via] || via || '';
}

function WhoList({ who, onPick }) {
  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
      <h4 style={{ margin: '0 0 0.5rem' }}>{who.length} {who.length === 1 ? 'person' : 'people'}</h4>
      {who.length === 0 ? (
        <div style={{ fontSize: '13px', color: '#6b7280' }}>Nothing matches these filters.</div>
      ) : (
        <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
          {who.slice(0, 200).map((row) => (
            <button
              key={row.user_id}
              type="button"
              onClick={() => onPick(row.user_id)}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '12px',
                width: '100%',
                textAlign: 'left',
                border: 'none',
                borderBottom: '1px solid #f3f4f6',
                background: 'white',
                padding: '6px 4px',
                cursor: 'pointer',
                fontSize: '13px',
              }}
            >
              <span>
                <strong>{row.display_name}</strong>
                {row.role_name ? <span style={{ color: '#6b7280' }}> · {row.role_name}</span> : null}
              </span>
              <span style={{ color: '#374151' }}>{viaLabel(row.via)}</span>
            </button>
          ))}
          {who.length > 200 && <div style={{ fontSize: '12px', color: '#6b7280', paddingTop: '6px' }}>+{who.length - 200} more. Narrow the filters.</div>}
        </div>
      )}
    </div>
  );
}

export default PermissionTesterTab;
